import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { MDText } from 'i18n-react';

import { Button, WarningIcon } from '@motorway/mw-highway-code';
import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { approveSaleFinalisation } from 'PrivateAPI';

import { formatPriceTransactional } from 'Utilities/formatPrice';
import { UrlFallbackContext } from 'Utilities/hooks/useUrlVRM';
import { FINANCE_PAYMENT_DETAILS_ROUTE } from 'Utilities/routing';
import { withViewTransition } from 'Utilities/viewTransitions';

import { PAYMENT_PAGE_EVENTS } from '../../../../../utilities/analytics/analyticsPaymentPage';
import { PAYMENT_STATUS_ROUTE_NAMES } from '../../../documentCapture/DocumentCapture.helpers';
import { usePaymentLongPooling } from '../../../payments/PaymentPending/usePaymentLongPooling';
import { PaymentStatus } from '../../../payments/Payments.types';
import { CollectionDetails } from '../../../vehicleCollection/CollectionDetails/CollectionDetails';
import { usePaymentDetailsSub } from '../../../vehicleCollection/hooks/usePaymentDetailsSub';
import { useSaleDetailsSub } from '../../../vehicleCollection/hooks/useSaleDetailsSub';
import { useVehicleCollectionSub } from '../../../vehicleCollection/hooks/useVehicleCollectionSub';
import { PaymentDetailsState } from '../../../vehicleCollection/VehicleCollectionSubjects.types';
import { ChevronPageHeader } from '../../components/ChevronPageHeader/ChevronPageHeader';
import { ConfirmNewOfferModal } from '../../components/ConfirmNewOfferModal/ConfirmNewOfferModal';
import { Frame } from '../../components/Frame/Frame';
import { HeadingBanner } from '../../components/HeadingBanner/HeadingBanner';
import { PaymentErrorButton } from '../../components/PaymentErrorButton/PaymentErrorButton';
import { PostSaleLayout } from '../../components/PostSaleLayout/PostSaleLayout';
import { ProcessingPaymentModal } from '../../components/ProcessingPaymentModal/ProcessingPaymentModal';

import LocalTexts from './VehiclePaymentDetails.texts.json';

import styles from './VehiclePaymentDetails.module.scss';

const LocalT = new MDText(LocalTexts);

const SUCCESS_STATUSES_SET = new Set<PaymentStatus>(['done']); // , 'not_required']);
const FAILURE_STATUSES_SET = new Set<PaymentStatus>(['errored', 'failed']);

const PaymentPending: React.FC<{[k in 'onSuccess' | 'onError']: () => void }> = ({ onError, onSuccess }) => {
  const { longPoolingStatus, paymentLongPoolingResult } = usePaymentLongPooling();

  useEffect(() => {
    if (longPoolingStatus === 'MAX_RETRIES') {
      onError?.();
      return;
    }

    if (longPoolingStatus === 'SUCCESS') {
      if (SUCCESS_STATUSES_SET.has(paymentLongPoolingResult as PaymentStatus)) {
        onSuccess?.();
        return;
      }

      if (FAILURE_STATUSES_SET.has(paymentLongPoolingResult as PaymentStatus)) {
        onError?.();
      }
    }
  }, [longPoolingStatus, onError, onSuccess, paymentLongPoolingResult]);

  return (
    <Frame className={styles.loader}>
      <LoadingIndicator showText size='large' text='We are processing your payment' variant='arrow' />
    </Frame>
  );
};

type ModalState = 'closed' | 'confirm' | 'processing' | 'success' | 'error';

export const VehiclePaymentDetails = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [localPaymentPageStatus, setLocalPaymentPageStatus] = useState<Nilable<PaymentDetailsState['paymentPageStatus']>>();
  const { push } = useHistory();
  const [modalState, setModalState] = useState<ModalState>('closed');

  const { dealershipName, hasNegativeEquity } = useSaleDetailsSub();
  const { collectionDetails, enquiryId, isNESettled, vrm } = useVehicleCollectionSub();
  const { offerAmount, paymentPageStatus } = usePaymentDetailsSub();

  const { transportProvider } = collectionDetails?.data?.collection ?? {};
  const collector = transportProvider?.name || dealershipName;

  const status = localPaymentPageStatus ?? paymentPageStatus;

  const sentryOpts = {
    extra: {
      enquiryId,
      hasNegativeEquity,
      isNESettled,
      localPaymentPageStatus,
      paymentPageStatus,
    },
    level: 'error',
    tags: {
      page: 'Vehicle Payment Details',
    },
  };

  const onModalOpen = () => {
    PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_CONTINUE_WITH_OFFER_MODAL_OPENED({
      buttonLabel: String(LocalT.translate('page.cta')),
      vrm,
    });

    withViewTransition(() => setModalState('confirm'));
  };

  const onModalClose = () => {
    PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_CONFIRM_NEW_OFFER_MODAL_CLOSED(vrm);

    withViewTransition(() => setModalState('closed'));
  };

  const onConfirm = async () => {
    PAYMENT_PAGE_EVENTS.VEHICLE_PAYMENT_ACCEPT_NEW_OFFER_BUTTON_CLICKED({
      buttonLabel: String(LocalT.translate('buttons.accept')),
      vrm,
    });

    setLoading(true);

    withViewTransition(() => setModalState('processing'));

    try {
      await approveSaleFinalisation(enquiryId);

      if (hasNegativeEquity && !isNESettled) {
        setModalState('closed');
        push(`/${vrm}${FINANCE_PAYMENT_DETAILS_ROUTE}`);
      } else {
        setLocalPaymentPageStatus('pending');
      }
    } catch (e) {
      setModalState('closed');
      setLocalPaymentPageStatus('error');

      // eslint-disable-next-line no-console
      globalThis?.Sentry?.captureException?.(e, sentryOpts);
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = useCallback(() => {
    setModalState('success');
  }, []);

  const onError = useCallback(() => {
    if (modalState === 'processing') {
      setModalState('closed');
    }
    setLocalPaymentPageStatus('error');
  }, [modalState]);

  const onViewSaleSummary = () => {
    window.location.href = `/${vrm}/${PAYMENT_STATUS_ROUTE_NAMES.SALE_COMPLETE}`;
  };

  const renderStatusContent = () => {
    switch (status) {
    case 'entry':
      return (
        <Button
          disabled={loading}
          icon='arrow'
          label={LocalT.translate('page.cta')}
          loading={loading}
          onClick={onModalOpen}
        />
      );
    case 'pending':
      return <PaymentPending onError={onError} onSuccess={onSuccess} />;
    case 'error':
      return <PaymentErrorButton />;
    default:
      return null;
    }
  };

  const renderModal = () => {
    if (modalState === 'closed') {
      return null;
    }

    switch (modalState) {
    case 'confirm':
      return (
        <ConfirmNewOfferModal
          className={styles.modalTransition}
          disabled={loading}
          onConfirm={onConfirm}
          onReject={onModalClose}
        />
      );

    case 'processing':
    case 'success':
      return (
        <ProcessingPaymentModal
          className={styles.modalTransition}
          onClose={onModalClose}
          onViewSaleSummary={onViewSaleSummary}
          state={modalState}
          vrm={vrm}
        />
      );

    default:
      return null;
    }
  };

  const isErrorState = status === 'error';

  const bannerDescription = isErrorState
    ? LocalT.translate('page.error.description')
    : LocalT.translate('page.description', { collector });

  const bannerIcon = isErrorState
    ? () => <div className={styles.warningIcon}><WarningIcon /></div>
    : undefined;

  const bannerTitle = LocalT.translate(`${isErrorState ? 'page.error.title' : 'page.title'}`);

  return (
    <UrlFallbackContext.Provider value={{ useUrlFallback: true }}>
      <PostSaleLayout header={(
        <ChevronPageHeader>
          <div className={styles.label}>{LocalT.translate('header.label')}</div>
          <div className={styles.figure}>{formatPriceTransactional(offerAmount)}</div>
        </ChevronPageHeader>
      )}>
        <HeadingBanner
          description={bannerDescription}
          icon={bannerIcon}
          title={bannerTitle}
        />
        {renderStatusContent()}
        <Frame>
          <CollectionDetails />
        </Frame>
        {renderModal()}
      </PostSaleLayout>
    </UrlFallbackContext.Provider>
  );
};
